class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this)); 
    this.setHeaderCartIconAccessibility();

    // Aybak change: remember which Add to Cart button was used, so renderContents()
    // can show the short "added" state on it. Capture phase, so it runs before product-form.js.
    document.addEventListener('submit', (evt) => {
      const form = evt.target;
      if (!form || !form.closest || !form.closest('product-form')) return;
      this.lastAddButton = evt.submitter || form.querySelector('[type="submit"]');
    }, true);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      // console.log(cartLink);
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }


  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('animate', 'active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      // sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      const oldProgressWidth = sectionElement.querySelector('.wbship .determinate')?.style.width || '0%';
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      const progressBar = sectionElement.querySelector('.wbship .determinate');
      if (progressBar) {
        progressBar.animate([{ width: oldProgressWidth }, { width: progressBar.style.width }], { duration: 1200, easing: 'ease' });
      }
    }));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      // Aybak change: the drawer used to open here after every Add to Cart (this.open()),
      // which also locked page scroll (body.overflow-hidden) and moved focus into the drawer.
      // It now stays closed; the drawer contents and header count above are still refreshed,
      // and the header cart icon still opens it via open(cartLink). Re-apply after a theme update.
      try { this.showAddedState(); } catch (e) { /* the "added" text is optional */ }
    });
  }

  // Aybak change (new): show "1 item added" on the Add to Cart button for about 1.5 seconds.
  // The text is laid over the button and the original content is only hidden (visibility),
  // so the button keeps its exact size. The button gets aria-live so screen readers hear it.
  showAddedState() {
    const ADDED_MS = 1500;
    const addedText = this.dataset.addedText;
    let button = this.lastAddButton;
    this.lastAddButton = null;
    // Quick add: the modal's button is gone by now; use the card button that opened the modal.
    if (!button || !button.isConnected) button = this.activeElement;
    if (!addedText || !button || !button.isConnected || button.tagName !== 'BUTTON') return;

    if (button.cartAddedTimer) {
      clearTimeout(button.cartAddedTimer);
    } else {
      button.cartAddedState = {
        children: Array.from(button.children).map((child) => [child, child.style.visibility]),
        position: button.style.position,
        ariaLive: button.getAttribute('aria-live'),
      };
      button.cartAddedState.children.forEach(([child]) => { child.style.visibility = 'hidden'; });
      if (getComputedStyle(button).position === 'static') button.style.position = 'relative';
      if (!button.cartAddedState.ariaLive) button.setAttribute('aria-live', 'polite');
      const message = document.createElement('span');
      message.className = 'cart-added-message';
      message.style.cssText = 'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;white-space:nowrap;overflow:hidden;pointer-events:none;';
      message.textContent = addedText;
      button.appendChild(message);
      button.cartAddedState.message = message;
      button.classList.add('cart-added');
    }

    button.cartAddedTimer = setTimeout(() => {
      const state = button.cartAddedState;
      state.message.remove();
      state.children.forEach(([child, visibility]) => { child.style.visibility = visibility; });
      button.style.position = state.position;
      if (!state.ariaLive) button.removeAttribute('aria-live');
      button.classList.remove('cart-added');
      button.cartAddedTimer = null;
      button.cartAddedState = null;
    }, ADDED_MS);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
